import { useState, useRef, useEffect, useCallback } from "react";
import { consumeReturnTo, consumeLifetimeReturnTo } from "@/lib/auth-return-to";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage, resetSessionCache } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Loader2, ShieldCheck, Copy, CheckCircle2, Download, ArrowLeft } from "lucide-react";
import logoImg from "@assets/Padpulse_logo_horizontal_nobg.png";
import { usePageTitle } from "@/hooks/use-page-title";

interface EnrollStartResponse {
  secret: string;
  qrCode: string;
}

interface EnrollConfirmResponse {
  success: boolean;
  backupCodes: string[];
  pendingInvitationCount?: number;
  user: {
    id: number;
    email: string;
    name: string | null;
    approved: boolean;
    status?: string;
    phone?: string | null;
    onboardingCompletedAt?: string | null;
    onboardingWizardCompletedAt?: string | null;
  };
}

export default function AppTotpEnroll() {
  usePageTitle("Set Up Two-Factor Auth");
  const [step, setStep] = useState<"qr" | "confirm" | "backup">("qr");
  const [totpCode, setTotpCode] = useState(["", "", "", "", "", ""]);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loginData, setLoginData] = useState<EnrollConfirmResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const [sessionProbeState, setSessionProbeState] = useState<"checking" | "ready">("checking");
  const probingRef = useRef(false);
  const navigatedRef = useRef(false);

  const probeSession = useCallback(async () => {
    if (probingRef.current || navigatedRef.current) return;
    probingRef.current = true;
    try {
      const res = await fetch("/api/app/profile", { credentials: "include" });
      if (res.ok && !navigatedRef.current) {
        navigatedRef.current = true;
        resetSessionCache();
        const profile = await res.json();
        navigate(consumeReturnTo(profile?.id ?? profile?.user?.id ?? null) ?? "/app/dashboard");
        return;
      }
    } catch {
      // Network error — fall through to show the form
    } finally {
      probingRef.current = false;
    }
    setSessionProbeState("ready");
  }, [navigate]);

  useEffect(() => {
    probeSession();
  }, [probeSession]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        probeSession();
      }
    };
    const handleFocus = () => {
      probeSession();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [probeSession]);

  const enrollStartQuery = useQuery<EnrollStartResponse>({
    queryKey: ["/api/app/auth/totp/enroll-start"],
    queryFn: async () => {
      const res = await fetch("/api/app/auth/totp/enroll-start", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to start enrollment");
      return res.json();
    },
  });

  const confirmMutation = useMutation<EnrollConfirmResponse, Error, string>({
    mutationFn: async (code: string) => {
      const res = await apiRequest("POST", "/api/app/auth/totp/enroll-confirm", { totpCode: code });
      return res.json() as Promise<EnrollConfirmResponse>;
    },
    onSuccess: (data) => {
      setCodeError(null);
      setBackupCodes(data.backupCodes);
      setLoginData(data);
      setStep("backup");
    },
    onError: (err: Error) => {
      const message = parseErrorMessage(err, "Invalid code. Please try again.");
      setCodeError(message);
      toast({ title: "Invalid code", description: message, variant: "destructive" });
      setTotpCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    },
  });

  const handleTotpChange = (index: number, value: string) => {
    // A multi-character value means a password manager (1Password) or browser
    // autofill dropped the whole code into one box without firing a paste
    // event. Strip any separators (e.g. "123 456") and distribute the digits
    // across all boxes so autofill fills the whole code.
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, 6);
      if (!digits) return;
      if (codeError) setCodeError(null);
      const newCode = ["", "", "", "", "", ""];
      for (let i = 0; i < 6; i++) newCode[i] = digits[i] || "";
      setTotpCode(newCode);
      inputRefs.current[Math.min(digits.length, 5)]?.focus();
      if (digits.length === 6 && !confirmMutation.isPending) {
        confirmMutation.mutate(digits);
      }
      return;
    }
    // A single typed character (or an empty string from deletion): accept only
    // digits or a clear; ignore stray non-digit keystrokes.
    if (value && !/^\d$/.test(value)) return;
    if (codeError) setCodeError(null);
    const newCode = [...totpCode];
    newCode[index] = value;
    setTotpCode(newCode);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    const fullCode = newCode.join("");
    if (fullCode.length === 6 && !confirmMutation.isPending) {
      confirmMutation.mutate(fullCode);
    }
  };

  const handleTotpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !totpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    if (codeError) setCodeError(null);
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newCode = [...totpCode];
    for (let i = 0; i < 6; i++) {
      newCode[i] = pasted[i] || "";
    }
    setTotpCode(newCode);
    if (pasted.length === 6) confirmMutation.mutate(pasted);
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadBackupCodes = () => {
    const content = `PadPulse Admin Backup Codes\n\n${backupCodes.join("\n")}\n\nStore these codes somewhere safe. Each code can only be used once.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "padpulse-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFinish = () => {
    if (!loginData) return;
    resetSessionCache();
    const u = loginData.user;
    const lifetimeReturn = consumeLifetimeReturnTo(u?.id ?? null);
    if (lifetimeReturn) { navigate(lifetimeReturn); return; }
    if (u?.status === "waitlist") {
      navigate("/app/awaiting-approval");
    } else if (!u?.phone) {
      navigate("/app/onboarding");
    } else if (!u?.onboardingWizardCompletedAt) {
      // Task #1630 — Gate on the wizard-finished flag.
      navigate("/app/welcome");
    } else if ((loginData?.pendingInvitationCount ?? 0) > 0) {
      navigate("/app/accept-invitations");
    } else {
      navigate("/app/dashboard");
    }
  };

  if (sessionProbeState === "checking") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" data-testid="totp-enroll-session-checking">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-orange-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src={logoImg} alt="PadPulse" className="h-[50px] w-auto" data-testid="img-enroll-logo" />
        </div>

        <Card className="w-full p-8 shadow-xl border border-gray-100 bg-white/80 backdrop-blur-sm">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-brand" />
            </div>
          </div>

          {step === "qr" && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center" data-testid="text-enroll-title">
                Set Up Two-Factor Authentication
              </h1>
              <p className="text-gray-500 text-sm mb-6 text-center" data-testid="text-enroll-subtitle">
                Admin accounts require an authenticator app. Scan the QR code below with Google Authenticator, 1Password, or Authy.
              </p>

              {enrollStartQuery.isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-brand" />
                </div>
              ) : enrollStartQuery.error ? (
                <div className="text-center py-4">
                  <p className="text-red-600 text-sm mb-4">Failed to load QR code. Please try again.</p>
                  <Button variant="outline" asChild data-testid="button-enroll-error-back">
                    <Link href="/app/login">Back to login</Link>
                  </Button>
                </div>
              ) : enrollStartQuery.data ? (
                <>
                  <div className="flex justify-center mb-4">
                    <img
                      src={enrollStartQuery.data.qrCode}
                      alt="TOTP QR Code"
                      className="w-48 h-48 border border-gray-200 rounded-lg"
                      data-testid="img-totp-qr"
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center mb-4">
                    Can't scan? Enter this code manually:
                  </p>
                  <div className="bg-gray-100 rounded-lg px-4 py-2 text-center mb-6">
                    <span className="font-mono text-sm text-gray-700 break-all" data-testid="text-totp-secret">
                      {enrollStartQuery.data.secret}
                    </span>
                  </div>
                  <Button
                    className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
                    onClick={() => setStep("confirm")}
                    data-testid="button-enroll-next"
                  >
                    I've Scanned the Code →
                  </Button>
                </>
              ) : null}

              <div className="text-center mt-4">
                <Button variant="ghost" size="sm" asChild className="text-gray-500 hover:text-gray-700" data-testid="button-enroll-back">
                  <Link href="/app/login"><ArrowLeft className="h-4 w-4 mr-1" /> Back to login</Link>
                </Button>
              </div>
            </>
          )}

          {step === "confirm" && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center" data-testid="text-confirm-title">
                Confirm Your Authenticator
              </h1>
              <p className="text-gray-500 text-sm mb-6 text-center" data-testid="text-confirm-subtitle">
                Enter the 6-digit code from your authenticator app to confirm setup.
              </p>

              <div className="flex gap-2.5 justify-center mb-2" onPaste={handlePaste}>
                {totpCode.map((digit, i) => (
                  <Input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleTotpChange(i, e.target.value)}
                    onKeyDown={(e) => handleTotpKeyDown(i, e)}
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    className={`w-12 h-14 text-center text-2xl font-bold ${codeError ? "border-red-500" : "border-gray-200 focus:border-brand"}`}
                    autoFocus={i === 0}
                    data-testid={`input-confirm-${i}`}
                  />
                ))}
              </div>
              {codeError && <p className="text-xs text-red-600 mb-4 text-center" data-testid="error-confirm-code">{codeError}</p>}
              {!codeError && <div className="mb-4" />}
              {confirmMutation.isPending && (
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-6 w-6 animate-spin text-brand" />
                </div>
              )}

              <div className="text-center">
                <Button variant="ghost" size="sm" onClick={() => setStep("qr")} className="text-gray-500 hover:text-gray-700" data-testid="button-confirm-back">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back to QR code
                </Button>
              </div>
            </>
          )}

          {step === "backup" && (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 text-center" data-testid="text-backup-title">
                Save Your Backup Codes
              </h1>
              <p className="text-gray-500 text-sm mb-4 text-center" data-testid="text-backup-subtitle">
                Save these codes somewhere safe. Each backup code can only be used once if you lose access to your authenticator app.
              </p>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="font-mono text-sm text-gray-700 bg-white border border-gray-200 rounded px-3 py-1.5 text-center" data-testid={`backup-code-${i}`}>
                      {code}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 mb-6">
                <Button variant="outline" className="flex-1 text-sm" onClick={copyBackupCodes} data-testid="button-copy-backup">
                  {copied ? <CheckCircle2 className="h-4 w-4 mr-1.5 text-green-600" /> : <Copy className="h-4 w-4 mr-1.5" />}
                  {copied ? "Copied!" : "Copy All"}
                </Button>
                <Button variant="outline" className="flex-1 text-sm" onClick={downloadBackupCodes} data-testid="button-download-backup">
                  <Download className="h-4 w-4 mr-1.5" />
                  Download
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6">
                <p className="text-xs text-amber-800 font-medium">
                  ⚠️ These codes will not be shown again. Store them in a password manager or a safe place.
                </p>
              </div>

              <Button
                className="w-full bg-brand hover:bg-brand-hover text-white font-semibold"
                onClick={handleFinish}
                data-testid="button-finish-enroll"
              >
                I've Saved My Codes — Continue
              </Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
